import { YFC } from '@/utils/constants';
import { AxiosInstance } from 'axios';
import { classToPlain } from 'class-transformer';
import { BaseService, StorageManager } from 'yellowfin-ui-lib';
import { Customer, Product, Wishlist } from 'yf-commerce-model-ui-lib';

export class WishlistService extends BaseService {

	private _items: Wishlist[] | null = null;;
	private _products: Product[] = [];

	private user?: Customer;

	private skipLoginWarning: boolean = false;

	constructor(http: AxiosInstance) {
		super(http);
	}

	initShop(user?: Customer): Promise<void> {
		this.user = user;
		if (this.user) {
			return this.merge();
		}
		return Promise.resolve();
	}

	async getProducts(): Promise<Product[]> {
		const ids = JSON.parse(StorageManager.get(YFC.WISHLIST_COOKIE));
		const wis = await this.mapResponse<Product[]>(this.http.post(`/ws/catalog/product/fromids`, ids), Product);
		if (wis) {
			this._products = wis;
		} else {
			this._products = [];
			this.setProducts();
		}
		return this._products;
	}

	setProducts(): void {
		StorageManager.set(YFC.WISHLIST_COOKIE, JSON.stringify((this._products.map(p => p.id))));
	}

	async merge(): Promise<void> {
		if (this._products && this._products.length && !this.isMerged()) {
			const data = await this.mapResponse<Wishlist[]>(this.http.post(`/ws/wishlist/merge`, classToPlain(this.mergeProducts())), Wishlist);
			this._items = data;
			this.readWishlist(data);
			StorageManager.set(YFC.WISHLIST_MERGED_COOKIE, 'true');
		} else {
			await this.getProducts()
			this.setProducts();

		}
	}

	private mergeProducts(): Wishlist[] {
		const wl: Wishlist[] = [];
		for (const p of this._products) {
			wl.push(new Wishlist(this.user!.id, p.id));
		}
		return wl;
	}

	private readWishlist(data: Wishlist[]) {
		const wl: Product[] = [];
		for (const w of data) {
			if (w.product) {
				wl.push(w.product);
			}
		}
		this._products = wl;
		this.setProducts();
		return wl;
	}

	clearData(): void {
		this._items = null;
		this._products = [];
		StorageManager.remove(YFC.WISHLIST_COOKIE)
		StorageManager.remove(YFC.WISHLIST_MERGED_COOKIE);
	}

	add(product: Product): Promise<boolean> {
		this.addProduct(product);
		const warning = !this.user && this._products.length == 1 && !this.skipLoginWarning;
		if (this.user) {
			const wl = new Wishlist(this.user.id, product.id);
			return new Promise((resolve, reject) => {
				this.mapResponse(this.http.post(`/ws/wishlist`, classToPlain(wl)), Wishlist)
					.then(() => resolve(warning)).catch((error) => reject(error));
			});
		}
		return Promise.resolve(warning);
	}

	remove(productid: number): Promise<void> {
		this.skipLoginWarning = true;
		this.removeProduct(productid);
		if (this.user) {
			const wl = this._items && this._items.find(p => p.productid === productid);
			if (wl) {
				return this.mapResponse(this.http.delete(`/ws/wishlist/${wl.id}`), Wishlist);
			}
		}
		return Promise.resolve();
	}

	isIn(pid: number): boolean {
		if (!this._products.length) {
			return false;
		}
		return (this._products.find(wl => wl.id === pid)) ? true : false;
	}

	private removeProduct(productid: number) {
		let ps = this._products;
		ps = ps.filter(p => p.id !== productid);
		this._products = ps;
		this.setProducts();
	}


	private addProduct(product: Product) {
		const wishlistItem = this._products.find(item => item.id === product.id);
		if (!wishlistItem) {
			this._products.push(product);
		}
		this.setProducts();
	}

	private isMerged() {
		return StorageManager.get(YFC.WISHLIST_MERGED_COOKIE) === 'true';
	}
}


